import { useMutation } from '@apollo/client';
import { Forbidden } from '@risksmart-app/components/src/errors/errors';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useGetOptionalGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { AddRiskToEnterpriseRiskDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import type { RiskFormDataFields } from 'src/pages/risks/forms/riskSchema';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import Loading from '@/components/loading';
import { useInsertRisk } from '@/hooks/mutations';
import { useGetRiskById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { riskDetailsUrl } from '@/utils/urls';

import RiskForm from '../../forms/RiskForm';

const RiskCreateTab: FC = () => {
  useI18NSummaryHelpContent('risks.help');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tierParam = searchParams.get('tier');
  const parentRiskIdParam = useGetOptionalGuidParam('riskId');

  const {
    data: parentRiskData,
    loading: parentRiskLoading,
    error: parentRiskError,
  } = useGetRiskById({
    queryArgs: { riskId: parentRiskIdParam ?? '' },
    shouldSkip: !parentRiskIdParam,
  });
  const parentRisk = parentRiskData?.risk?.[0] ?? null;

  const { hasPermission: canInsertRisk, loading: permissionLoading } =
    useHasPermissionQuery('insert:risk', parentRisk);

  const { insertRisk } = useInsertRisk();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const { t } = useTranslation(['common']);
  const { addNotification } = useNotifications();
  const [addRiskToEntity] = useMutation(AddRiskToEnterpriseRiskDocument);

  if (parentRiskIdParam && (parentRiskLoading || permissionLoading)) {
    return <Loading />;
  }

  if (parentRiskIdParam && parentRiskError) {
    throw new Forbidden('Access denied');
  }

  if (parentRiskIdParam && !parentRisk) {
    throw new Forbidden('Parent risk not found');
  }

  if (parentRiskIdParam && !canInsertRisk) {
    throw new Forbidden('Access denied');
  }

  const onSave = async (riskFormData: RiskFormDataFields) => {
    const data = await insertRisk({
      DepartmentTypeIds:
        riskFormData.departments?.map((d) => d.DepartmentTypeId) || [],
      TagTypeIds: riskFormData.tags?.map((t) => t.TagTypeId) || [],
      Tier: riskFormData.Tier,
      Title: riskFormData.Title,
      Description: riskFormData.Description,
      Treatment: riskFormData.Treatment,
      Status: riskFormData.Status,
      CustomAttributeData: riskFormData.CustomAttributeData ?? null,
      ParentRiskId: riskFormData.ParentRiskId ?? null,
      schedule: riskFormData.schedule,
      ...ownerContributorAndViewerIds(riskFormData),
    });
    const newRiskId = data?.insertChildRisk?.Id;
    if (newRiskId && riskFormData.EntityId) {
      try {
        await addRiskToEntity({
          variables: {
            objects: [{ EntityId: riskFormData.EntityId, RiskId: newRiskId }],
          },
        });
      } catch {
        // The risk itself was created — surface the link failure but still
        // navigate, otherwise resubmitting the form would duplicate the risk.
        addNotification({
          type: 'error',
          content: t('risks.fields.entity_link_failed'),
        });
      }
    }
    if (newRiskId) {
      navigate(riskDetailsUrl(newRiskId), { replace: true });
    }

    return { savedId: newRiskId };
  };

  const onDismiss = (saved?: boolean) => {
    if (!saved) {
      navigate(-1);
    }
  };

  return (
    <RiskForm
      onSave={onSave}
      onDismiss={onDismiss}
      initialTier={
        tierParam ? Number(tierParam) : parentRiskIdParam ? 2 : undefined
      }
      initialParentRiskId={parentRiskIdParam}
      isParentRiskIdLocked={!!parentRiskIdParam}
      publishToolbarState={objectToolbarEnabled}
    />
  );
};

export default RiskCreateTab;
